/**
 * Botón de descarga de archivos del backend con autenticación JWT (blob).
 *
 * Reemplaza el anti-patrón `<a href={apiDownloadUrl(...)} target="_blank">`, que
 * abre el endpoint del API en una pestaña nueva SIN el header Authorization → el
 * backend rechaza la petición y el navegador termina cayendo al SPA (dashboard).
 * Ver patrón de visores PDF/XML autenticados (PRs #212/#213).
 */
import { useState, type ReactNode } from 'react'
import { Download, Loader2 } from 'lucide-react'
import { apiDownload } from '@/lib/apiClient'
import { ToastContainer, useToast } from '@/components/Toast'
import { extractErrorMessage } from '@/lib/errorUtils'

interface Props {
  path: string
  filename: string
  label?: ReactNode
  className?: string
  iconSize?: number
  dataTour?: string
  disabled?: boolean
  /** El nombre lo decide el backend; `filename` queda de respaldo. */
  usarNombreDelServidor?: boolean
}

const DEFAULT_CLS =
  'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50'

export default function DownloadButton({
  path, filename, label = 'Descargar CSV', className = DEFAULT_CLS, iconSize = 14, dataTour,
  disabled = false, usarNombreDelServidor = false,
}: Props) {
  const toast = useToast()
  const [loading, setLoading] = useState(false)

  const onClick = async () => {
    setLoading(true)
    try {
      await apiDownload(path, filename, { usarNombreDelServidor })
    } catch (e: any) {
      toast.error(extractErrorMessage(e, 'No se pudo generar la descarga.'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <button onClick={onClick} disabled={loading || disabled} className={className} data-tour={dataTour}>
        {loading ? <Loader2 size={iconSize} className="animate-spin" /> : <Download size={iconSize} />} {label}
      </button>
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}
